import React, { useState, useEffect } from "react";
import { TbKey, TbTrash, TbPencil, TbPlus, TbAlertTriangle } from "react-icons/tb";
import { usePasskeys, passkeysSupported, platformAuthenticatorAvailable, secureContext } from "../../hooks/usePasskeys";
import { fmtDate } from "../../lib/dates";
import { Button, Alert, Modal, ConfirmModal } from "../../components";

// Passkeys, rendered inside Profile the way two-factor is.
//
// A passkey signs you in on its own — no password and no code — because the device
// holding the key already verified you with a biometric or PIN. That is worth stating on
// the screen: someone who reads "passkey" as "another second factor" will not understand
// why there is no password step afterwards.
//
// The default name is the one thing here that guesses. A person with three passkeys needs
// to tell them apart to revoke the right one, and "Passkey 2" tells them nothing, so the
// platform is used as a starting point and the field stays editable.

const defaultName = () => {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad/.test(ua)) return "iPhone or iPad";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows Hello";
  if (/Android/.test(ua)) return "Android device";
  return "Passkey";
};

const PasskeysSection = () => {
  const { passkeys, enabled, loading, busy, error, setError, add, rename, remove } = usePasskeys();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [removing, setRemoving] = useState(null);

  const supported = passkeysSupported();

  // Asked once, on mount. Null while unknown, so nothing is claimed before the answer
  // arrives — a warning that flashes and disappears is worse than a slightly late one.
  const [platformAuth, setPlatformAuth] = useState(null);
  useEffect(() => { let live = true;
    platformAuthenticatorAvailable().then((v) => { if (live) setPlatformAuth(v); });
    return () => { live = false; };
  }, []);

  const openAdd = () => { setName(defaultName()); setError(""); setAdding(true); };

  const confirmAdd = async () => {
    const ok = await add(name.trim() || defaultName());
    if (ok) setAdding(false);
  };

  return (
    <div className="v2-settings-group">
      <div className="v2-section-title">Passkeys</div>
      <p className="v2-hint" style={{ margin: "0 0 .75rem" }}>
        Sign in with your fingerprint, face or device PIN instead of a password. A passkey
        replaces both your password and your two-factor code — the device proves it is
        you before it will sign in.
      </p>

      <Alert>{error}</Alert>

      {!enabled && (
        <Alert tone="warning">
          Passkeys are not configured on this server yet. An administrator needs to set the
          passkey domain before they can be used.
        </Alert>
      )}
      {enabled && !supported && (
        <Alert tone="warning">
          This browser does not support passkeys. Try a current version of Chrome, Safari,
          Edge or Firefox.
        </Alert>
      )}

      {/* Only catches a genuinely insecure origin (http://). It does NOT catch an invalid
          certificate: Chromium reports isSecureContext as true there and still refuses the
          ceremony, so that case is detected from the error message instead — see
          humanError() in hooks/usePasskeys.js. */}
      {enabled && supported && !secureContext() && (
        <Alert tone="warning">
          This page is not a secure context, so your browser will refuse to create a
          passkey. Open the site over HTTPS with a valid certificate and try again.
        </Alert>
      )}

      {enabled && supported && platformAuth === false && (
        <Alert tone="warning">
          This device has no built-in way to verify you — no fingerprint reader, face
          unlock or device PIN is set up. A passkey needs one. Set up Touch ID, Windows
          Hello or a screen lock, or use a hardware security key or your phone instead.
        </Alert>
      )}

      {loading ? (
        <p className="v2-hint">Loading…</p>
      ) : passkeys.length === 0 ? (
        <p className="v2-hint">No passkeys yet. Add one and you can sign in without a password.</p>
      ) : (
        <div className="v2-pk-list">
          {passkeys.map((p) => (
            <div key={p.id} className={`v2-pk-row${p.stale ? " v2-pk-row--stale" : ""}`}>
              <span className="v2-pk-icon"><TbKey size={17} /></span>
              <div className="v2-pk-main">
                <div className="v2-pk-name">{p.name}</div>
                <div className="v2-pk-meta">
                  Added {fmtDate(p.created_at)}
                  {p.last_used_at ? ` · Last used ${fmtDate(p.last_used_at)}` : " · Never used"}
                  {p.backed_up ? " · Synced" : ""}
                </div>
                {/* A credential is bound to the domain it was made on and can never be
                    moved. Saying so here is the only warning anyone gets before it fails
                    at a sign-in. */}
                {p.stale && (
                  <div className="v2-pk-stale">
                    <TbAlertTriangle size={13} /> Created for {p.rp_id} — it will not work
                    on this address. Remove it and add a new one.
                  </div>
                )}
                {/* Explicitly 0, not merely falsy: null means the browser never reported,
                    which is normal on older browsers and not a problem. */}
                {p.discoverable === 0 && (
                  <div className="v2-pk-stale">
                    <TbAlertTriangle size={13} /> This device stored it in a way that
                    cannot sign you in without a password. Remove it and try another
                    device or browser.
                  </div>
                )}
              </div>
              <Button icon aria-label={`Rename ${p.name}`}
                      onClick={() => setRenaming({ id: p.id, name: p.name })}>
                <TbPencil size={15} />
              </Button>
              <Button icon variant="danger" aria-label={`Remove ${p.name}`}
                      onClick={() => setRemoving(p)}>
                <TbTrash size={15} />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: ".75rem" }}>
        <Button variant="primary" disabled={!enabled || !supported || busy} onClick={openAdd}>
          <TbPlus size={15} /> {busy ? "Waiting for your device…" : "Add a passkey"}
        </Button>
      </div>

      {/* Naming happens BEFORE the system dialog, not after: once the browser prompt is
          dismissed the credential already exists, and asking then means an unnamed
          passkey if the person walks away. */}
      {adding && (
        <Modal title="Add a passkey" onClose={() => setAdding(false)}
               footer={<>
                 <Button onClick={() => setAdding(false)}>Cancel</Button>
                 <Button variant="primary" disabled={busy} onClick={confirmAdd}>
                   {busy ? "Waiting for your device…" : "Continue"}
                 </Button>
               </>}>
          <p className="v2-hint" style={{ marginTop: 0 }}>
            Your browser will ask you to confirm with your fingerprint, face or device PIN.
            Name it so you can tell it apart from your other devices later.
          </p>
          <input className="v2-input" value={name} autoFocus aria-label="Passkey name"
                 maxLength={120}
                 onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter" && !busy) confirmAdd(); }} />
        </Modal>
      )}

      {renaming && (
        <Modal title="Rename passkey" onClose={() => setRenaming(null)}
               footer={<>
                 <Button onClick={() => setRenaming(null)}>Cancel</Button>
                 <Button variant="primary" disabled={!renaming.name.trim()}
                         onClick={async () => { await rename(renaming.id, renaming.name.trim()); setRenaming(null); }}>
                   Save
                 </Button>
               </>}>
          <input className="v2-input" value={renaming.name} autoFocus aria-label="Passkey name"
                 maxLength={120}
                 onChange={(e) => setRenaming((r) => ({ ...r, name: e.target.value }))} />
        </Modal>
      )}

      {removing && (
        <ConfirmModal
          title={`Remove ${removing.name}?`}
          confirmLabel="Remove passkey"
          onClose={() => setRemoving(null)}
          onConfirm={async () => { await remove(removing.id); setRemoving(null); }}
          body={
            <>
              That device will no longer be able to sign you in.
              {passkeys.length === 1 && " This is your only passkey — you will need your password again."}
            </>
          }
        />
      )}
    </div>
  );
};

export default PasskeysSection;
